import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import {getEventStatus} from "../../../api/eventStatusAPI";
import { ConvertOIDValueToString } from "../../../utils/objectConverter";
import { useTranslation } from "react-i18next";
import {
  renderAlarmm,
  renderWarningg,
  renderNormalGray,
} from "../alarm-history/StatusChip";
import { useRefreshInterval } from "../../../contexts/RefreshIntervalContext";
import { usePageVisibility } from "../../../hooks/usePageVisibility";
import { getPlaceIdFromPathname } from "../../../utils/pathUtils";
import { useObjectData } from "../../../hooks/useLocalStorageData";
import { getCurrentTenant } from "../../../config/tenantConfig";
import { getProjectDashboardObjectCodes } from "../../../api/projectAPI";
import { getMuiDataGridLocaleText } from "../../../i18n/muiDataGridLocale";

export default function ActiveAlarmsDataGrid({
  onCountsChange,
  refreshInterval,
}) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isPageVisible = usePageVisibility();
  const [alarms, setAlarms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { interval, isRunning } = useRefreshInterval();
  // Use custom hook to get objectData from localStorage
  const objectData = useObjectData();
  const projectCode = getCurrentTenant().projectCode;
  const isRcu = projectCode === "RCU";
  const shouldLeftAlignGrid = projectCode === "VENDAI";
  // RCU: base rows come from the project's projectDashboardJSon codes — the same
  // source the main-panel MiniDataGrid uses, so the two stay in sync.
  const [dashboardObjectCodes, setDashboardObjectCodes] = useState([]);

  useEffect(() => {
    if (!isRcu) return;
    (async () => {
      try {
        const codes = await getProjectDashboardObjectCodes();
        if (Array.isArray(codes)) setDashboardObjectCodes(codes);
      } catch (e) {
        console.error("getProjectDashboardObjectCodes failed:", e);
      }
    })();
  }, [isRcu]);

  const columns = useMemo(
    () => [
      {
        field: "eventName",
        headerName: t("alarmHistory.eventName"),
        flex: 1.5,
        minWidth: 120,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "status",
        headerName: t("alarmHistory.status"),
        flex: 1,
        minWidth: 100,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          if (params.value === "Alarm") {
            return renderAlarmm(params.value, t);
          } else if (params.value === "Warning") {
            return renderWarningg(params.value, t);
          } else if (params.value === "Normal") {
            return renderNormalGray(params.value, t);
          }
          return null;
        },
      },
      {
        field: "value",
        headerName: t("alarmHistory.value"),
        flex: 1,
        minWidth: 100,
        align: "center",
        headerAlign: "center",
      },
    ],
    [t]
  );

  const rawId = getPlaceIdFromPathname(location.pathname);
  const node_id = rawId ? parseInt(rawId, 10) : null;

  const fetchAlarms = useCallback(async () => {
    if (isLoading) return;
    if (!isRunning) {
      // When stopped, don't fetch new data but keep existing data
      return;
    }
    // Sayfa görünür değilse veri çekme
    if (!isPageVisible) return;

    const currentTime = Date.now();
    if (currentTime - lastFetchTime < 5000) return;

    setIsLoading(isInitialLoad);
    setLastFetchTime(currentTime);

    try {
      // RCU: base rows are the project's projectDashboardJSon codes (same source
      // as the main-panel MiniDataGrid). Every code always renders a row;
      // alarms/warnings from getEventStatus are overlaid on top, and codes
      // without an alarm stay "Normal" with value "-".
      if (isRcu && dashboardObjectCodes.length > 0) {
        const result = await getEventStatus();
        const nodeItems = result.filter(
          (item) =>
            item.nodeId === node_id && dashboardObjectCodes.includes(item.event)
        );

        // For each code keep the most severe matching item (alarm > warning > normal).
        const byCode = new Map();
        nodeItems.forEach((item) => {
          const existing = byCode.get(item.event);
          if (!existing || item.alarmType > existing.alarmType) {
            byCode.set(item.event, item);
          }
        });

        const rows = dashboardObjectCodes.map((code) => {
          const match = objectData.find((d) => d.code === code);
          const item = byCode.get(code);
          let status = "Normal";
          let value = "-";
          if (item && item.alarmType === 1) {
            status = "Warning";
          } else if (item && item.alarmType === 2) {
            status = "Alarm";
          }
          if (item && item.alarmType !== 0) {
            value = ConvertOIDValueToString({ Type: match?.type }, item.value);
          }
          return {
            id: `${node_id}-${code}`,
            eventName: match?.name || code,
            value,
            status,
          };
        });

        const warnings = rows.filter((r) => r.status === "Warning").length;
        const alerts = rows.filter((r) => r.status === "Alarm").length;
        const normals = rows.filter((r) => r.status === "Normal").length;
        if (onCountsChange) {
          onCountsChange({ warnings, alerts, normals });
        }

        setAlarms(rows);
        return;
      }

      const DASHBOARD_EVENTS = [
        "Otomat Durumu",
        "POS Ünitesi",
        "Nakit Üniteleri",
        "Satış Durumu",
        "Sıcaklık",
        "Sinyal Düzeyi",
        "Stok Seviyesi",
        "Kapı Durumu",
      ];
      const allowedEventIds = new Set(
        objectData
          .filter((obj) => DASHBOARD_EVENTS.includes(obj.name))
          .map((obj) => obj.code)
      );

      const result = await getEventStatus();

      const nodeFiltered = result
        .filter((item) => item.nodeId === node_id)
        .filter((item) => allowedEventIds.has(item.event));

      const warnings = nodeFiltered.filter(
        (item) => item.alarmType === 1
      ).length;
      const alerts = nodeFiltered.filter((item) => item.alarmType === 2).length;
      const normals = nodeFiltered.filter(
        (item) => item.alarmType === 0
      ).length;

      if (onCountsChange) {
        onCountsChange({ warnings, alerts, normals });
      }

      const mappedData = nodeFiltered.map((item) => {
        const match = objectData.find((d) => d.code === item.event);

        let status;
        if (item.alarmType === 0) {
          status = "Normal";
        } else if (item.alarmType === 1) {
          status = "Warning";
        } else if (item.alarmType === 2) {
          status = "Alarm";
        } else {
          status = "Unknown";
        }

        const date = new Date(item.epoch * 1000).toLocaleString();

        const convertedValue = ConvertOIDValueToString(
          { Type: match?.type },
          item.value
        );

        return {
          ...item,
          id: `${item.nodeId}-${item.event}-${item.epoch}`,
          eventName: match?.name || item.event,
          value: convertedValue,
          status: status,
          description: convertedValue,
          date: date,
        };
      });

      setAlarms(mappedData);
    } catch (error) {
      console.error("Error fetching alarms:", error);
    } finally {
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  }, [
    node_id,
    onCountsChange,
    isLoading,
    lastFetchTime,
    isRunning,
    objectData,
    isInitialLoad,
    isPageVisible,
    isRcu,
    dashboardObjectCodes,
  ]);

  // fetchAlarms kimliği her fetch'te değişiyor (state + prop bağımlılıkları).
  // Polling effect'ini bu değişimden koruyup istek yağmurunu önlemek için ref'te tut.
  const fetchAlarmsRef = useRef(fetchAlarms);
  useEffect(() => {
    fetchAlarmsRef.current = fetchAlarms;
  }, [fetchAlarms]);

  useEffect(() => {
    // Paused olsa bile veriyi bir kez çek; tekrarlı polling sadece running iken.
    const initialFetchTimeout = setTimeout(() => {
      fetchAlarmsRef.current();
    }, 500);

    if (!isRunning) {
      // Durdurulduğunda yeni veri çekme, mevcut veriyi koru.
      return () => clearTimeout(initialFetchTimeout);
    }

    const intervalMs = Math.max((interval || 15) * 1000, 1000);
    const intervalId = setInterval(() => {
      fetchAlarmsRef.current();
    }, intervalMs);

    return () => {
      clearTimeout(initialFetchTimeout);
      clearInterval(intervalId);
    };
  }, [interval, isRunning]);

  // Memoize columns to prevent unnecessary re-renders
  const memoizedColumns = useMemo(
    () =>
      shouldLeftAlignGrid
        ? columns.map((column) => ({
            ...column,
            align: "left",
            headerAlign: "left",
          }))
        : columns,
    [columns, shouldLeftAlignGrid]
  );
  return (
    <Box
      sx={{
        height: { xs: 400, sm: 400, md: "100%" },
        width: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <DataGrid
        rows={alarms}
        columns={memoizedColumns}
        loading={isLoading}
        hideFooter={true}
        disableRowSelectionOnClick
        density="compact"
        columnVisibilityModel={{
          description: { xs: false, sm: true },
          date: { xs: true },
        }}
        sx={{
          "& .MuiDataGrid-cell": {
            whiteSpace: "normal",
            lineHeight: "normal",
            padding: "8px",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "background.paper",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
          },
          "& .MuiDataGrid-main": {
            width: "100%",
            overflow: "auto",
          },
          "& .MuiDataGrid-virtualScroller": {
            overflow: "auto",
          },
        }}
        localeText={getMuiDataGridLocaleText(i18n.language || i18n.resolvedLanguage, {
          noRowsLabel: t("activeAlarms.noAlarms", "No active alarms"),
        })}
        initialState={{
          sorting: {
            sortModel: [
              {
                field: "status",
                sort: "asc",
              },
            ],
          },
        }}
        sortingOrder={["asc", "desc"]}
        sortingMode="client"
        sortModel={[{ field: "status", sort: "asc" }]}
        sortComparator={(v1, v2, cellParams, mode) => {
          if (cellParams.field === "status") {
            const statusOrder = { Alarm: 0, Warning: 1, Normal: 2, Unknown: 3 };
            return statusOrder[v1] - statusOrder[v2];
          }
          return v1 > v2 ? 1 : v1 < v2 ? -1 : 0;
        }}
        getRowClassName={(params) => {
          return params.row.status === "Alarm"
            ? "alarm-row"
            : params.row.status === "Warning"
            ? "warning-row"
            : "";
        }}
      />
    </Box>
  );
}
